import type { AxiosRequestConfig } from "axios";
import isEmpty from "lodash/isEmpty";
import { useMobileStore } from "mobile/src/stores/mobile";
import { storeToRefs } from "pinia";
import { computed, inject, ref, toRefs } from "vue";
import { useRouter } from "vue-router";

import { ErrorDialog } from "shared/boot/alert";
import { track } from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import { isBroadcastProviderUnsupported } from "shared/helpers/broadcastProvider";
import {
  openExternalUrl,
  openExternalUrlForMention,
} from "shared/helpers/external";
import { mentionKeywords } from "shared/helpers/mentions";
import { safePush } from "shared/helpers/routing";
import { isSocialStream } from "shared/helpers/stream";
import DialogService from "shared/modals/DialogService";
import ModalService from "shared/modals/ModalService";
import { IsPublicInsightsSymbol } from "shared/modules/Charts/types/symbols";
import { type AppConfig } from "shared/services/appConfig/types";
import features from "shared/services/features";
import {
  getBroadcastMediaRequest,
  hasArchivedBroadcastContent,
  type OpenTvEyesMentionModalOptions,
  openTveyesPlayerModal,
  openViewMentionDetailWithTvEyesModal,
} from "shared/services/mention";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";
import type { Stream } from "shared/types";
import type {
  Mention,
  MentionOrCustomerArticle,
  TvEyesMention,
} from "shared/types/mentions";
import {
  hasPublicLink,
  isKinetiqMention,
  isMagazineArticle,
  isMention,
  isMentionOutsideSourceRetentionPeriod,
  isMentionRequiresAllowanceCheck,
  isMentionWithAppRedirect,
  isMentionWithBody,
  isMentionWithDatahub,
  isMentionWithMediatrack,
  isPaperArticle,
  isSocialMention,
  isTvEyesMention,
} from "shared/types/mentions/guards";

export interface Emit {
  (event: "click", mention: MentionOrCustomerArticle): void;
}

interface DetailOptions {
  defaultTab?: string;
  forceFullBroadcast?: boolean;
  redirect?: boolean;
  viewMention?: boolean;
}

const mentionResourceTypes: Partial<
  Record<MentionOrCustomerArticle["type"], string>
> = {
  radio_story: "radio_stories",
  tv_story: "tv_stories",
};

export default function useMention(
  props?: {
    mention?: MentionOrCustomerArticle;
    stream?: Stream;
  },
  context?: { emit: Emit }
) {
  const router = useRouter();
  const safeRouterPush = safePush({ router });

  const isAdminMode = inject<boolean>("isAdminMode", false);
  const isMobile = inject<boolean>("isMobile", false);
  const isPublicInsights = inject<boolean>(IsPublicInsightsSymbol, false);

  const { mention = ref(null), stream = ref<Stream>() } = toRefs(props ?? {});
  const loadingMention = ref(false);

  const { setSelectedMention } = useMobileStore();

  const userStore = useUserStore();
  const { isAdminUser } = storeToRefs(userStore);

  const globalStore = useGlobalStore();
  const { appConfig, isStreem } = storeToRefs(globalStore);

  const isSocialStreamComputed = computed(() => isSocialStream(stream.value));

  const mentionType = computed(() => mention.value?.type || "");

  const medium = computed(() => mention.value?.medium?.toLowerCase() || "");

  const keywords = computed(() =>
    mention.value && isMention(mention.value)
      ? mentionKeywords(mention.value)
      : []
  );

  const playsOnTveyes = computed(() =>
    Boolean(
      mention.value &&
      isMention(mention.value) &&
      isTvEyesMention(mention.value)
    )
  );

  const isSocial = computed(() => {
    const currentMention = mention.value;
    if (!currentMention || !isMention(currentMention)) return false;

    return isSocialMention(currentMention);
  });

  const isDatahub = computed(
    () =>
      mention.value &&
      isMention(mention.value) &&
      isMentionWithDatahub(mention.value!) &&
      mention.value.datahub
  );

  async function fetchMention({
    id,
    options = {},
    type,
  }: {
    id: MentionOrCustomerArticle["id"];
    options?: AxiosRequestConfig;
    type: MentionOrCustomerArticle["type"];
  }): Promise<MentionOrCustomerArticle> {
    const resourceType = mentionResourceTypes[type] ?? `${type}s`;

    const { data } = await $streemApiV1.get(`${resourceType}/${id}`, options);

    return data;
  }

  async function loadMentionDetails(
    loadMention: MentionOrCustomerArticle
  ): Promise<MentionOrCustomerArticle> {
    const fetchedMention = await fetchMention({
      id: loadMention.id,
      type: loadMention.type,
    });

    return { ...loadMention, ...fetchedMention } as MentionOrCustomerArticle;
  }

  function trackClick(trackedMention: MentionOrCustomerArticle) {
    const title = isSocialStreamComputed.value
      ? "Social Stream"
      : "Mention Stream";

    const type =
      trackedMention.type === "external_item" ? "External Item" : "Mention";

    track(`Clicked on ${type} in ${title}`, {
      id: trackedMention.id,
      reactScore:
        isMention(trackedMention) &&
        "factmata_enrichment" in trackedMention &&
        trackedMention.factmata_enrichment
          ? Math.round(
              Number(trackedMention.factmata_enrichment?.risk_score) * 100
            )
          : null,
      streamId: stream.value?.id,
      type: trackedMention.type,
    });
  }

  function shouldOpenExternalUrl(
    loadedMention: MentionOrCustomerArticle,
    viewMention?: boolean
  ): boolean {
    if (
      isMention(loadedMention) &&
      isMentionWithAppRedirect(loadedMention) &&
      loadedMention.in_app_redirect &&
      (!isAdminUser.value || (isAdminUser.value && !viewMention))
    ) {
      return true;
    }

    if (["external_item", "customer_article"].includes(mentionType.value)) {
      return true;
    }

    if (["tv", "radio", "podcast"].includes(medium.value) || isDatahub.value) {
      return false;
    }

    if (
      isMention(loadedMention) &&
      isMentionWithBody(loadedMention) &&
      !viewMention
    ) {
      return Boolean(!loadedMention.body && !loadedMention.translated_body);
    }

    return !viewMention;
  }

  function openViewMentionDetailModal(
    printMention: Mention,
    {
      defaultTab,
      forceFullBroadcast,
    }: Pick<DetailOptions, "defaultTab" | "forceFullBroadcast"> = {}
  ) {
    const modal =
      isAdminMode && !isKinetiqMention(printMention)
        ? "PaperPreviewModal"
        : "ViewMentionDetailModal";

    ModalService.open(modal, {
      props: {
        mention: printMention,
        ...(isEmpty(stream?.value) ? {} : { stream: stream.value }),
        ...(defaultTab ? { defaultTab } : {}),
        ...(forceFullBroadcast ? { forceFullBroadcast } : {}),
      },
    });
  }

  function closeViewMentionAlertModal() {
    ModalService.close("ViewMentionAlertModal");
  }

  function closeMediatrackConfirmModal() {
    ModalService.close("ConfirmModal");
  }

  function continueToDetail(
    loadedMention: MentionOrCustomerArticle,
    detailOptions: DetailOptions = {}
  ) {
    const { defaultTab, forceFullBroadcast, redirect, viewMention } =
      detailOptions;

    if (shouldOpenExternalUrl(loadedMention, viewMention)) {
      openExternalUrlForMention(loadedMention, router, redirect);

      return;
    }

    if (
      isAdminMode &&
      isMention(loadedMention) &&
      isKinetiqMention(loadedMention)
    ) {
      openViewMentionDetailModal(loadedMention, {
        defaultTab,
        forceFullBroadcast,
      });

      return;
    }

    if (isAdminMode && medium.value === "tv") return;

    if (isMobile && stream?.value?.id) {
      setSelectedMention(loadedMention);

      safeRouterPush({
        name: "mention-detail",
        params: {
          mentionId: loadedMention.id,
          mentionType: loadedMention.type,
          streamId: stream.value.id,
        },
        query: defaultTab ? { tab: defaultTab } : {},
      });
    } else if (isMention(loadedMention) && isTvEyesMention(loadedMention)) {
      openModalForTvEyesMention(loadedMention, {
        forceFullBroadcast,
        stream: stream.value,
      });
    } else {
      openViewMentionDetailModal(loadedMention as Mention, {
        defaultTab,
        forceFullBroadcast,
      });
    }
  }

  function openViewMentionAlertModal(
    alertMention: Mention,
    viewMention?: boolean
  ) {
    ModalService.open("ViewMentionAlertModal", {
      events: {
        cancel: () => closeViewMentionAlertModal(),
        continue: async () => {
          closeViewMentionAlertModal();

          try {
            const loadedMention = await loadMentionDetails(alertMention);
            continueToDetail(loadedMention, { viewMention });
          } catch (error) {
            ErrorDialog("Unable to load Media Item", error);
          }
        },
      },
      props: {
        mention: alertMention,
      },
    });
  }

  function openMediatrackConfirmModal() {
    const { platformName } = appConfig.value as AppConfig;

    ModalService.open("ConfirmModal", {
      events: {
        confirm: () => closeMediatrackConfirmModal(),
      },
      props: {
        confirmLabel: "Close",
        hideCancel: true,
        message: getLocaleText(
          "modals.mediatrack_international_print_modal.message",
          {
            platformName,
          }
        ),
        title: getLocaleText(
          "modals.mediatrack_international_print_modal.title"
        ),
      },
    });
  }

  function isMentionNotClickable(currentMention: Mention) {
    return (
      isMentionOutsideSourceRetentionPeriod(currentMention) &&
      currentMention.outside_source_retention_period
    );
  }

  async function mentionClicked(
    clickedMention: MentionOrCustomerArticle,
    viewMention?: boolean,
    options: {
      defaultTab?: string;
      emitClick?: boolean;
    } = {}
  ) {
    if (loadingMention.value) {
      return;
    }

    try {
      trackClick(clickedMention);

      // If we're in Public Insights, we only allow opening mentions with a public link
      // or we do nothing. In Australia, we also allow opening magazine and paper articles
      // but in a slightly different way. As the content restrictions are stricter, we redirect them
      // to the internal link so they can read the article only if they have access to the app.
      if (isPublicInsights) {
        if (isMention(clickedMention)) {
          if (
            isStreem.value &&
            (isMagazineArticle(clickedMention) ||
              isPaperArticle(clickedMention))
          ) {
            window.open(clickedMention.internal_link, "_blank");
          } else if (hasPublicLink(clickedMention)) {
            window.open(clickedMention.public_link, "_blank");
          }
        }

        return;
      }

      loadingMention.value = true;

      if (
        isMention(clickedMention) &&
        isMentionWithMediatrack(clickedMention) &&
        clickedMention.mediatrack_international_print
      ) {
        openMediatrackConfirmModal();

        return;
      }

      if (isMention(clickedMention) && isMentionNotClickable(clickedMention))
        return;

      if (options.emitClick !== false) {
        context?.emit("click", clickedMention);
      }

      if (isSocial.value) {
        openExternalUrlForMention(clickedMention, router);

        return;
      }

      if (isMention(clickedMention) && isTvEyesMention(clickedMention)) {
        const forceFullBroadcast =
          hasArchivedBroadcastContent(clickedMention) && viewMention;

        openModalForTvEyesMention(clickedMention, {
          forceFullBroadcast,
          mediaRequest: getBroadcastMediaRequest(clickedMention) ?? undefined,
          stream: stream.value,
          trackTvEyesMediaRequestStatus: true,
        });

        return;
      }

      let needsRecording = false;

      if (
        isMention(clickedMention) &&
        isMentionRequiresAllowanceCheck(clickedMention) &&
        clickedMention.requires_allowance_check
      ) {
        needsRecording = await features.requiresActivityRecord(clickedMention);
      }

      if (isMention(clickedMention) && needsRecording) {
        openViewMentionAlertModal(clickedMention, viewMention);

        return;
      }

      const loadedMention = await loadMentionDetails(clickedMention);

      if (
        isMention(loadedMention) &&
        isPaperArticle(loadedMention) &&
        loadedMention.permission === false
      ) {
        DialogService.alert({
          confirmLabel: "Read on AFR with subscription",
          title: "Unauthorised",
        }).onOk(() => {
          openExternalUrl(loadedMention.url);
        });

        return;
      }

      if (
        isMention(loadedMention) &&
        (isMagazineArticle(loadedMention) || isPaperArticle(loadedMention)) &&
        loadedMention.expired_article
      ) {
        DialogService.alert({
          confirmLabel: "Close",
          title: "This content is not available",
        }).onOk(() => {});

        return;
      }

      continueToDetail(loadedMention, { ...options, viewMention });
    } catch (error) {
      ErrorDialog("Unable to load Media Item", error);
    } finally {
      loadingMention.value = false;
    }
  }

  function openModalForTvEyesMention(
    tvEyesMention: TvEyesMention,
    options: OpenTvEyesMentionModalOptions
  ) {
    if (isBroadcastProviderUnsupported(tvEyesMention)) {
      openViewMentionDetailWithTvEyesModal(tvEyesMention, {
        ...options,
        mediaRequest: undefined,
        trackTvEyesMediaRequestStatus: false,
      });
    } else if (isMobile || isAdminMode) {
      openTveyesPlayerModal(tvEyesMention, options);
    } else {
      openViewMentionDetailWithTvEyesModal(tvEyesMention, options);
    }
  }

  function viewMentionDetail(
    loadedMention: MentionOrCustomerArticle,
    forceFullBroadcast?: boolean
  ) {
    continueToDetail(loadedMention, {
      forceFullBroadcast,
      redirect: true,
      viewMention: true,
    });
  }

  return {
    fetchMention,
    isMentionNotClickable,
    isSocialMention: isSocial,
    keywords,

    medium,
    mentionClicked,
    mentionType,
    openModalForTvEyesMention,
    playsOnTveyes,
    viewMentionDetail,
  };
}
